import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS, resolvePublicFileUrl } from "../../constants/api-urls";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { isLogisticsGrnAcceptStageComplete } from "../../utils/logisticsRfqGrnGate";
import { partyAddressLinesForInvoice } from "../../utils/invoiceAddressFormat";
import "../../assets/styles/generate_invoice.scss";
import "../../assets/styles/logistics_generate_invoice.scss";

function extractApiData(res) {
  const body = res?.data ?? res ?? {};
  return body?.data !== undefined ? body.data : body;
}

function extractReceivedRows(res) {
  const data = extractApiData(res);
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
}

function pickRow(rows, submissionId) {
  const sid = Number(submissionId);
  return rows.find((x) => Number(x?.id) === sid) || null;
}

/** Normalize list row enough for GRN gate + reference (matches inbox list shape). */
function normalizeListRow(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id != null ? Number(row.id) : NaN;
  if (!Number.isFinite(id) || id <= 0) return null;
  const dpRaw = row.deliveryProcess ?? row.delivery_process ?? null;
  const rawStages = dpRaw?.selectedStages ?? dpRaw?.selected_stages ?? null;
  const stagesArr = Array.isArray(rawStages) ? rawStages : [];
  const rawProg = dpRaw?.stageProgress ?? dpRaw?.stage_progress ?? null;
  let prog = {};
  if (rawProg != null && typeof rawProg === "object" && !Array.isArray(rawProg)) prog = rawProg;
  else if (typeof rawProg === "string" && rawProg.trim()) {
    try {
      const j = JSON.parse(rawProg);
      if (j && typeof j === "object" && !Array.isArray(j)) prog = j;
    } catch {
      /* ignore */
    }
  }
  const deliveryProcess =
    dpRaw != null && typeof dpRaw === "object" && !Array.isArray(dpRaw)
      ? {
          selectedStages: stagesArr.map((s) => String(s)),
          stageProgress: prog,
          currentStageKey: dpRaw.currentStageKey ?? dpRaw.current_stage_key ?? null,
        }
      : null;
  return {
    id,
    rfqReferenceNumber: String(row.rfqReferenceNumber ?? row.rfq_reference_number ?? "").trim(),
    quoteStatus: row.quoteStatus ?? row.quote_status ?? null,
    shippingManifestAccepted: Boolean(row.shippingManifestAccepted ?? row.shipping_manifest_accepted),
    logisticsGrnId: row.logisticsGrnId ?? row.logistics_grn_id ?? null,
    deliveryProcess,
  };
}

function nestedContact(entity) {
  if (!entity || typeof entity !== "object") return null;
  if (entity.contact_details && typeof entity.contact_details === "object") return entity.contact_details;
  if (entity.contactDetails && typeof entity.contactDetails === "object") return entity.contactDetails;
  return null;
}

function registrationBlock(entity) {
  if (!entity || typeof entity !== "object") return {};
  const c = entity.certifications;
  if (!c || typeof c !== "object") return {};
  return (
    c.registrations ||
    c.companyRegistrations ||
    c.company_registrations ||
    {}
  );
}

/** GSTIN / tax id — matches `company_profile.tax_number` and logistics `certifications.registrations.gstin`. */
function pickGstinFromCompany(entity) {
  if (!entity || typeof entity !== "object") return "";
  const regs = registrationBlock(entity);
  return String(
    regs.gstin ||
      regs.GSTIN ||
      entity.gstin ||
      entity.gst_in ||
      entity.tax_number ||
      entity.taxNumber ||
      "",
  ).trim();
}

/** IEC — `company_profile.iec_number` or logistics registrations. */
function pickIecFromCompany(entity) {
  if (!entity || typeof entity !== "object") return "";
  const regs = registrationBlock(entity);
  return String(
    regs.iec ||
      regs.importExportCode ||
      regs.import_export_code ||
      entity.iec_number ||
      entity.iecNumber ||
      entity.iec ||
      entity.iec_code ||
      "",
  ).trim();
}

/**
 * Email / phone — company API uses `user_email` / `user_phone` and
 * `contact_details.{contact_email,contact_number}`; logistics profile uses
 * `contact_details.{primaryEmail,primaryPhone}`.
 */
function pickEmailPhone(entity) {
  const n = nestedContact(entity);
  const email = String(
    entity?.user_email ??
      entity?.userEmail ??
      entity?.email ??
      n?.contact_email ??
      n?.contactEmail ??
      n?.primary_email ??
      n?.primaryEmail ??
      n?.email ??
      "",
  ).trim();
  const phone = String(
    entity?.user_phone ??
      entity?.userPhone ??
      entity?.phone ??
      entity?.mobile ??
      n?.contact_number ??
      n?.contactNumber ??
      n?.primary_phone ??
      n?.primaryPhone ??
      n?.phone ??
      n?.mobile ??
      "",
  ).trim();
  return { email: email || "—", phone: phone || "—" };
}

/** Merge `/company/my-profile` with `/company/logistics/my-profile` so invoice shows logistics contact + registrations. */
function mergeLogisticsProviderForInvoice(companyPd, logisticsLp) {
  const c = companyPd && typeof companyPd === "object" ? { ...companyPd } : {};
  const lp = logisticsLp && typeof logisticsLp === "object" ? logisticsLp : null;
  if (!lp) return c;

  const lcd = lp.contact_details && typeof lp.contact_details === "object" ? lp.contact_details : {};
  const ccd = c.contact_details && typeof c.contact_details === "object" ? { ...c.contact_details } : {};
  const cert = lp.certifications && typeof lp.certifications === "object" ? lp.certifications : {};
  const regs =
    (cert.registrations && typeof cert.registrations === "object" ? cert.registrations : null) ||
    (cert.companyRegistrations && typeof cert.companyRegistrations === "object" ? cert.companyRegistrations : null) ||
    (cert.company_registrations && typeof cert.company_registrations === "object" ? cert.company_registrations : null) ||
    {};
  const bi = lp.basic_information && typeof lp.basic_information === "object" ? lp.basic_information : {};
  const cCert = c.certifications && typeof c.certifications === "object" ? c.certifications : {};
  const cRegs =
    (cCert.registrations && typeof cCert.registrations === "object" ? cCert.registrations : null) ||
    (cCert.companyRegistrations && typeof cCert.companyRegistrations === "object" ? cCert.companyRegistrations : null) ||
    (cCert.company_registrations && typeof cCert.company_registrations === "object" ? cCert.company_registrations : null) ||
    {};

  const media = typeof c.media === "object" && c.media !== null ? { ...c.media } : {};
  const lpLogo = bi.companyLogo || bi.company_logo;
  if (lpLogo) {
    media.company_logo = lpLogo;
  }

  return {
    ...c,
    company_name: c.company_name || c.companyName || bi.companyName || bi.company_name,
    contact_details: { ...ccd, ...lcd },
    media,
    certifications: {
      ...cCert,
      ...cert,
      registrations: {
        ...cRegs,
        ...regs,
      },
    },
  };
}

/** Match Seller `generate_invoice.js` currency option resolution. */
function resolveCurrencyDisplay(quoteView, currencyOptions) {
  const raw =
    quoteView?.currency ??
    quoteView?.currency_code ??
    quoteView?.currencyCode ??
    quoteView?.quote_currency_id ??
    "USD";
  const rawStr = String(raw);
  if (!currencyOptions?.length) {
    return { code: rawStr, symbol: rawStr, label: rawStr };
  }
  const found = currencyOptions.find(
    (opt) =>
      (opt.currencyCode && String(opt.currencyCode).toUpperCase() === rawStr.toUpperCase()) ||
      (opt.value != null && String(opt.value) === rawStr) ||
      (opt.label && String(opt.label).toUpperCase().includes(rawStr.toUpperCase())),
  );
  const code = found?.currencyCode || rawStr;
  const symbol = found?.currencySymbol || code;
  const label = found?.label || code;
  return { code, symbol, label };
}

function formatAmountWithSymbol(amount, currencyDisplay) {
  const n = Number(amount);
  const safe = Number.isFinite(n) ? n : 0;
  const formatted = safe.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sym = currencyDisplay.symbol;
  if (sym === "€" || sym === "£") return `${formatted} ${sym}`;
  return `${sym}${formatted}`;
}

export default function LogisticsGenerateInvoicePage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const sid = submissionId != null ? Number(submissionId) : NaN;
  const { currencyOptions } = useCurrencyMaster();

  const [loading, setLoading] = useState(true);
  const [rfqRef, setRfqRef] = useState("");
  const [quoteView, setQuoteView] = useState(null);
  const [buyerCompany, setBuyerCompany] = useState(null);
  const [providerProfile, setProviderProfile] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState(
    "Charges are based on the accepted logistics quotation and completed GRN acceptance milestone.",
  );
  const [taxPercent, setTaxPercent] = useState("0");
  const [savingInvoice, setSavingInvoice] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(sid) || sid <= 0) {
      toast.error("Invalid RFQ");
      navigate("/dashboard/logistics/rfqs", { replace: true });
      return;
    }
    setLoading(true);
    try {
      const [listRes, quoteRes, buyerRes, profileRes, logisticsRes] = await Promise.all([
        CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_RECEIVED_FOR_PROVIDER),
        CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_QUOTE(sid)),
        CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_BUYER_COMPANY(sid)),
        CommonService.get("/company/my-profile"),
        CommonService.get("/company/logistics/my-profile").catch(() => null),
      ]);

      const rawRows = extractReceivedRows(listRes);
      const rows = rawRows.map(normalizeListRow).filter(Boolean);
      const row = pickRow(rows, sid);
      if (!row) {
        toast.error("RFQ not found in your inbox.");
        navigate("/dashboard/logistics/rfqs", { replace: true });
        return;
      }
      if (!isLogisticsGrnAcceptStageComplete(row)) {
        toast.error("Generate invoice is available after GRN acceptance is completed.");
        navigate("/dashboard/logistics/rfqs", { replace: true });
        return;
      }
      if (String(row.quoteStatus || "").trim().toLowerCase() !== "accepted") {
        toast.error("Quote must be accepted before generating an invoice.");
        navigate("/dashboard/logistics/rfqs", { replace: true });
        return;
      }

      try {
        const invRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_INVOICE(sid));
        const invPack = invRes?.data !== undefined ? invRes.data : invRes;
        const inv = invPack?.invoice ?? invPack;
        if (inv && inv.id != null && Number(inv.id) > 0) {
          toast.info("An invoice already exists for this RFQ. Use View invoice on the RFQ list.");
          navigate("/dashboard/logistics/rfqs", { replace: true });
          return;
        }
      } catch (invErr) {
        if (invErr?.response?.status !== 404) {
          throw invErr;
        }
      }

      const quotePack = extractApiData(quoteRes);
      const qv = quotePack?.quote;
      if (!qv || typeof qv !== "object") {
        throw new Error(quoteRes?.message || "Could not load your quotation.");
      }
      setQuoteView(qv);

      const buyerPack = extractApiData(buyerRes);
      setBuyerCompany(buyerPack?.buyerCompany ?? buyerPack ?? null);

      const profileBody = profileRes?.data ?? profileRes ?? {};
      const pd = profileBody?.data ?? profileBody;
      const logisticsBody = logisticsRes?.data ?? logisticsRes ?? {};
      const lp = logisticsBody?.data ?? logisticsBody;
      const lpObj = lp && typeof lp === "object" && lp.company_id != null ? lp : null;
      const mergedProvider =
        pd && typeof pd === "object" ? mergeLogisticsProviderForInvoice(pd, lpObj) : null;
      setProviderProfile(mergedProvider);

      const ymd = new Date().toISOString().slice(0, 10);
      setInvoiceDate(ymd);
      const d2 = new Date();
      d2.setDate(d2.getDate() + 30);
      setDueDate(d2.toISOString().slice(0, 10));
      setInvoiceNumber(`LGI-${row.rfqReferenceNumber || sid}-${ymd.replace(/-/g, "")}`);
      setRfqRef(row.rfqReferenceNumber || `RFQ-${sid}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Failed to load invoice data.");
      navigate("/dashboard/logistics/rfqs", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [sid, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const currencyDisplay = useMemo(
    () => resolveCurrencyDisplay(quoteView, currencyOptions),
    [quoteView, currencyOptions],
  );

  const subtotal = useMemo(() => {
    const ta = quoteView?.totalAmount ?? quoteView?.total_amount;
    const n = Number(ta);
    return Number.isFinite(n) ? n : 0;
  }, [quoteView]);

  const taxRate = useMemo(() => {
    const n = Number(taxPercent);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [taxPercent]);

  const taxAmount = useMemo(() => (subtotal * taxRate) / 100, [subtotal, taxRate]);
  const grandTotal = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  const providerName = providerProfile?.company_name ?? providerProfile?.companyName ?? "—";
  const buyerName = buyerCompany?.company_name ?? buyerCompany?.companyName ?? "—";
  const providerLines = partyAddressLinesForInvoice(providerProfile);
  const buyerLines = partyAddressLinesForInvoice(buyerCompany);
  const providerGstin = pickGstinFromCompany(providerProfile) || "—";
  const providerIec = pickIecFromCompany(providerProfile) || "—";
  const buyerTaxId =
    pickGstinFromCompany(buyerCompany) ||
    String(buyerCompany?.tax_id ?? buyerCompany?.taxId ?? "").trim() ||
    "—";
  const buyerPlace =
    (buyerCompany?.place_of_supply || buyerCompany?.placeOfSupply || buyerCompany?.state || "").trim() || "—";
  const provContact = pickEmailPhone(providerProfile);
  const buyContact = pickEmailPhone(buyerCompany);

  const logoSrc = useMemo(() => {
    const raw =
      providerProfile?.company_logo ??
      providerProfile?.companyLogo ??
      providerProfile?.media?.company_logo ??
      "";
    return resolvePublicFileUrl(raw);
  }, [providerProfile]);

  const handleGenerateInvoice = useCallback(async () => {
    if (!Number.isFinite(sid) || sid <= 0) return;
    const buyerCompanyIdRaw = buyerCompany?.company_id ?? buyerCompany?.companyId;
    const buyerCompanyIdNum =
      buyerCompanyIdRaw != null && String(buyerCompanyIdRaw).trim() !== "" && Number.isFinite(Number(buyerCompanyIdRaw))
        ? Number(buyerCompanyIdRaw)
        : null;
    const providerAddressStr = providerLines.length ? providerLines.join("\n") : "";
    const buyerAddressStr = buyerLines.length ? buyerLines.join("\n") : "";
    const body = {
      invoiceNumber: String(invoiceNumber || "").trim(),
      invoiceDate: String(invoiceDate || "").trim(),
      dueDate: dueDate ? String(dueDate).trim() : null,
      rfqReference: rfqRef || null,
      currencyCode: currencyDisplay.code || null,
      subtotal,
      taxPercent: taxRate,
      taxAmount,
      grandTotal,
      notes: notes || "",
      provider: {
        companyName: providerName,
        address: providerAddressStr,
        gstin: providerGstin !== "—" ? providerGstin : "",
        iec: providerIec !== "—" ? providerIec : "",
        email: provContact.email !== "—" ? provContact.email : "",
        phone: provContact.phone !== "—" ? provContact.phone : "",
      },
      buyer: {
        companyName: buyerName,
        address: buyerAddressStr,
        taxId: buyerTaxId !== "—" ? buyerTaxId : "",
        placeOfSupply: buyerPlace !== "—" ? buyerPlace : "",
        email: buyContact.email !== "—" ? buyContact.email : "",
        phone: buyContact.phone !== "—" ? buyContact.phone : "",
        companyId: buyerCompanyIdNum,
      },
      lineItems: [{ description: "Freight & logistics services", amount: subtotal }],
    };
    setSavingInvoice(true);
    try {
      const res = await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_SUBMISSION_INVOICE(sid), body);
      const inner = res?.data !== undefined ? res.data : res;
      const invId = inner?.invoiceId ?? inner?.invoice_id;
      const wasExisting = Boolean(inner?.existing);
      toast.success(
        res?.message ||
          (wasExisting
            ? "Invoice was already on file; no duplicate was created."
            : invId != null
              ? `Invoice #${invId} saved.`
              : "Invoice saved."),
      );
      navigate("/dashboard/logistics/rfqs", { replace: true });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Could not save invoice.");
    } finally {
      setSavingInvoice(false);
    }
  }, [
    sid,
    buyerCompany,
    providerLines,
    buyerLines,
    invoiceNumber,
    invoiceDate,
    dueDate,
    rfqRef,
    currencyDisplay.code,
    subtotal,
    taxRate,
    taxAmount,
    grandTotal,
    notes,
    providerName,
    providerGstin,
    providerIec,
    provContact.email,
    provContact.phone,
    buyerName,
    buyerTaxId,
    buyerPlace,
    buyContact.email,
    buyContact.phone,
    navigate,
  ]);

  if (loading) {
    return (
      <div className="generate-invoice logistics-gi-print">
        <div
          className="gi-main-content"
          style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}
        >
          <p>Loading invoice data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="generate-invoice logistics-gi-print">
      <div className="gi-main-content">
        <div className="alert alert-info small py-2 mb-3 no-print" role="note">
          <strong>Generate invoice</strong> saves invoice data to the database, then returns you to the RFQ list. If an
          invoice already exists for this shipment, the server returns the existing record and does not create a
          duplicate.
        </div>

        <div className="gi-header no-print">
          <div className="gi-header-left">
            <nav aria-label="breadcrumb" className="gi-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard/logistics" className="breadcrumb-link">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/dashboard/logistics/rfqs" className="breadcrumb-link">
                    RFQs received
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Generate Invoice
                </li>
              </ol>
            </nav>
            <h1 className="gi-main-title">Create Logistics Invoice</h1>
            <p className="gi-subtitle">Generate invoice from accepted logistics quotation after GRN acceptance</p>
          </div>
          <div className="gi-header-right">
            <div className="gi-action-buttons">
              <button type="button" className="gi-back-btn" onClick={() => navigate("/dashboard/logistics/rfqs")}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
                Back
              </button>
              <button
                type="button"
                className="gi-btn gi-btn-primary"
                onClick={handleGenerateInvoice}
                disabled={savingInvoice}
              >
                {savingInvoice ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                    Saving…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                    Generate invoice
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="gi-main-card">
          <div className="gi-invoice-header-section">
            <div className="gi-invoice-header-content">
              <div className="gi-invoice-header-left">
                <h2 className="gi-section-title">TAX INVOICE / LOGISTICS SERVICE</h2>
                <p className="gi-invoice-label">Original for Buyer</p>
              </div>
              <div className="gi-invoice-header-right">
                <div className="gi-logo-container">
                  {logoSrc ? (
                    <img src={logoSrc} alt="Company logo" className="gi-company-logo" />
                  ) : (
                    <div className="gi-logo-placeholder">Logo</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="gi-seller-buyer-grid">
            <div className="gi-seller-section">
              <div className="gi-section-header-with-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                  <path d="M10 6h4" />
                  <path d="M10 10h4" />
                  <path d="M10 14h4" />
                  <path d="M10 18h4" />
                </svg>
                <h3 className="gi-section-subtitle">Seller / Exporter Details</h3>
              </div>
              <div className="gi-seller-buyer-content">
                <div className="gi-company-name">{providerName}</div>
                <div className="gi-address-lines">
                  {providerLines.length ? (
                    providerLines.map((line, idx) => <p key={`p-a-${idx}`}>{line}</p>)
                  ) : (
                    <p>—</p>
                  )}
                </div>
                <div className="gi-divider" />
                <div className="gi-details-grid-2col">
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">GSTIN:</p>
                    <p className="gi-detail-value">{providerGstin}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">IEC:</p>
                    <p className="gi-detail-value">{providerIec}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Email:</p>
                    <p className="gi-detail-value">{provContact.email}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Phone:</p>
                    <p className="gi-detail-value">{provContact.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="gi-buyer-section">
              <div className="gi-section-header-with-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                  <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                  <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                  <path d="M10 6h4" />
                  <path d="M10 10h4" />
                  <path d="M10 14h4" />
                  <path d="M10 18h4" />
                </svg>
                <h3 className="gi-section-subtitle">Buyer / Consignee Details</h3>
              </div>
              <div className="gi-seller-buyer-content">
                <div className="gi-company-name">{buyerName}</div>
                <div className="gi-address-lines">
                  {buyerLines.length ? (
                    buyerLines.map((line, idx) => <p key={`b-a-${idx}`}>{line}</p>)
                  ) : (
                    <p>—</p>
                  )}
                </div>
                <div className="gi-divider" />
                <div className="gi-details-grid-2col">
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Tax ID:</p>
                    <p className="gi-detail-value">{buyerTaxId}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Place of Supply:</p>
                    <p className="gi-detail-value">{buyerPlace}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Email:</p>
                    <p className="gi-detail-value">{buyContact.email}</p>
                  </div>
                  <div className="gi-detail-item">
                    <p className="gi-detail-label">Phone:</p>
                    <p className="gi-detail-value">{buyContact.phone}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="gi-section gi-invoice-details-section">
            <div className="gi-form-grid-6col">
              <div className="gi-form-field">
                <label>Invoice Number *</label>
                <input
                  type="text"
                  className="input-control"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                />
              </div>
              <div className="gi-form-field">
                <label>Invoice Date *</label>
                <input
                  type="date"
                  className="input-control input-control-date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="gi-form-field">
                <label>Due Date *</label>
                <input
                  type="date"
                  className="input-control input-control-date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
              <div className="gi-form-field">
                <label>RFQ Reference</label>
                <input type="text" className="input-control" value={rfqRef} readOnly style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }} />
              </div>
              <div className="gi-form-field">
                <label>Submission ID</label>
                <input
                  type="text"
                  className="input-control"
                  value={String(sid)}
                  readOnly
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
              <div className="gi-form-field">
                <label>Currency</label>
                <input
                  type="text"
                  className="input-control"
                  value={currencyDisplay.label}
                  readOnly
                  style={{ backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
                />
              </div>
            </div>
          </div>

          <div className="gi-table-wrapper">
            <table className="gi-table">
              <thead>
                <tr>
                  <th className="gi-table-col-number">#</th>
                  <th className="gi-table-col-description">Description</th>
                  <th className="gi-table-col-total-header">Amount ({currencyDisplay.code})</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>
                    <div className="gi-table-description">
                      <div className="gi-table-text">Freight &amp; logistics services</div>
                      <div className="gi-table-text gi-table-text-warranty">
                        Accepted quotation for this RFQ, including completed shipment and GRN acceptance.
                      </div>
                    </div>
                  </td>
                  <td className="gi-table-col-total-cell">{formatAmountWithSymbol(subtotal, currencyDisplay)}</td>
                </tr>
                <tr className="gi-table-summary-row">
                  <td />
                  <td className="gi-table-summary-label">
                    <span className="font-bold">Subtotal:</span>
                  </td>
                  <td className="gi-table-summary-value">
                    <span className="font-semibold">{formatAmountWithSymbol(subtotal, currencyDisplay)}</span>
                  </td>
                </tr>
                <tr className="gi-table-summary-row gi-breakdown-blue no-print">
                  <td />
                  <td className="gi-table-summary-label">
                    <span className="font-bold">Tax % (screen only)</span>
                  </td>
                  <td className="gi-table-summary-value">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-control"
                      style={{ maxWidth: "120px", marginLeft: "auto" }}
                      value={taxPercent}
                      onChange={(e) => setTaxPercent(e.target.value)}
                    />
                  </td>
                </tr>
                <tr className="gi-table-summary-row gi-breakdown-blue">
                  <td />
                  <td className="gi-table-summary-label">
                    <span className="font-bold">Tax ({taxRate}%):</span>
                  </td>
                  <td className="gi-table-summary-value">
                    <span className="font-semibold">{formatAmountWithSymbol(taxAmount, currencyDisplay)}</span>
                  </td>
                </tr>
                <tr className="gi-table-summary-row gi-breakdown-green-bold gi-table-grand-total">
                  <td />
                  <td className="gi-table-summary-label">
                    <span className="font-bold">Grand Total:</span>
                  </td>
                  <td className="gi-table-summary-value">
                    <span className="font-bold">{formatAmountWithSymbol(grandTotal, currencyDisplay)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="gi-section gi-notes-section">
            <h4 className="gi-section-subtitle-small">Additional Notes &amp; Special Instructions</h4>
            <textarea
              className="gi-textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add remarks or billing notes (printed on invoice)…"
            />
          </div>

          <div className="gi-section gi-declaration-section">
            <h4 className="gi-declaration-title">Declaration</h4>
            <p className="gi-declaration-text">
              We declare that this invoice shows the actual price of the logistics services described and that all
              particulars are true and correct.
            </p>
          </div>

          <div className="gi-signatures-section">
            <div className="gi-signature-left">
              <p className="gi-signature-label">Buyer&apos;s Signature &amp; Stamp</p>
            </div>
            <div className="gi-signature-right">
              <p className="gi-signature-company">For {providerName}</p>
              <p className="gi-signature-label">Authorized Signatory</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
